import { type Dayjs } from "@marekl/adminer/datetime";
import type { MembershipRole, Person } from "../../../lib/queries/memberships";
import type { ID } from "../../../lib/id";

/** Where a draft row came from, which decides what the submit does with it. */
export type DraftOrigin =
  | { kind: "persisted"; membershipId: ID; personId: ID }
  | {
      kind: "transfer";
      personId: ID;
      sourceSeasonId: ID;
      /** Put back on the soupiska after a merge found them removed, not picked from the source season. */
      reregistered?: boolean;
    }
  | { kind: "new" };

/**
 * DraftPerson mirrors MemberFormValues["person"] field for field — including
 * birth_date as a Dayjs — so a dialog's MemberFormOutput drops straight into a
 * row with no adapter in between.
 */
export interface DraftPerson {
  first_name: string;
  last_name: string;
  country_code: string;
  birth_date: Dayjs | null;
  birth_number: string;
  street: string;
  city: string;
  postal_code: string;
  phone: string;
}

/** Dotted paths the grid and the server both use to address a cell. */
export type DraftFieldPath =
  | `person.${keyof DraftPerson}`
  | "roles"
  | "row";

export type RowErrors = Partial<Record<DraftFieldPath, string>>;

export interface DraftRow {
  /** Stable client token: "m:12" | "p:44" | "new:3". Never reused. */
  key: string;
  origin: DraftOrigin;
  /** Server truth to diff against; null for a person who does not exist yet. */
  baseline: Person | null;
  baselineRoles: MembershipRole[];
  person: DraftPerson;
  roles: MembershipRole[];
  /** Staged removal — the row stays visible so one click undoes it. */
  removed: boolean;
  isLeader: boolean;
  /** Errors the server bound to this row on the last rejected submit. */
  serverErrors: RowErrors;
  /**
   * Fields somebody else changed while this row changed them too, each with
   * the value now on the server (draftRebase). Neither side is preselected:
   * the row blocks the submit until each one is decided.
   */
  conflicts: Partial<Record<keyof DraftPerson, FieldConflict>>;
  /** Edited here, but the membership was closed meanwhile (draftRebase). */
  orphaned: boolean;
  /**
   * A typed-in person who meanwhile appeared on the roster under the same
   * name and birth date: the membership id to offer merging into.
   */
  duplicateOf: ID | null;
}

export interface FieldConflict {
  theirs: DraftPerson[keyof DraftPerson];
}

/** One line of „what the merge did", optionally pointing at a row. */
export interface RebaseNotice {
  key: string | null;
  message: string;
}

export interface DraftState {
  rows: DraftRow[];
  sourceSeasonId: ID | null;
  /**
   * True once the first HYDRATE ran. The signature alone cannot say so: an
   * empty roster's signature is "", indistinguishable from the initial
   * state — which is why a brand-new team never got its trailing blank row.
   */
  hydrated: boolean;
  /** The leader id the rows were last marked with (SET_LEADER re-marks). */
  leaderPersonId: ID | null;
  /**
   * Signature of the roster this draft was hydrated from. When a later
   * refetch disagrees the draft is stale — surfaced to the user rather than
   * silently overwritten, since overwriting would discard their typing.
   */
  rosterSignature: string;
  /** Monotonic; kept in state so the reducer stays pure. */
  nextNewId: number;
  /** Top-level message from a rejected submit. */
  formMessage: string | null;
  /** What the last merge with the server roster did (draftRebase). */
  rebaseNotices: RebaseNotice[];
}

export const emptyPerson: DraftPerson = {
  first_name: "",
  last_name: "",
  country_code: "CZ",
  birth_date: null,
  birth_number: "",
  street: "",
  city: "",
  postal_code: "",
  phone: "",
};
